<script setup lang="ts">
import { computed } from 'vue'
import { LibraryBig } from '@lucide/vue'
import { Progress } from 'catalyst-vue'
import { useLibraryStore } from '../../stores/library'
import { useLibraryEntriesStore } from '../../stores/library-entries'
import { useComicCover } from '../../lib/comic-cover'
import { collectionBadge, collectionProgress, collectionStatus, type CollectionCell } from '../../lib/collection-presentation'
import CollectionCardMenu, { type CollectionAction } from './collection-card-menu.vue'
import ComicSelectBox from './comic-select-box.vue'
import ComicStatusDot from './comic-status-dot.vue'

/** 列表檢視的集合列，與集合格同一套語意：多選時代表全部成員，否則開啟集合頁。 */
const props = defineProps<{ item: CollectionCell }>()
const emit = defineEmits<{ open: [cell: CollectionCell]; read: [cell: CollectionCell]; edit: [cell: CollectionCell]; dissolve: [cell: CollectionCell]; delete: [cell: CollectionCell] }>()
const store = useLibraryStore()
const entries = useLibraryEntriesStore()
const collection = computed(() => props.item.collection)
const coverScope = computed(() => props.item.cover.libraryId === 'cloud' ? 'cloud' as const : 'local' as const)
const { element, cover, failed } = useComicCover(() => props.item.cover, () => coverScope.value)
const selecting = computed(() => entries.selectionMode)
const selected = computed(() => entries.selection.has(props.item.id))
const status = computed(() => collectionStatus(props.item.members, store))
const progress = computed(() => collectionProgress(props.item))
const merged = computed(() => props.item.mode === 'merged')
/** 合併冊的右側數字：有進度顯示「第 N／M 頁」，沒有就只顯示總頁數。 */
const pagesText = computed(() => {
  if (!merged.value) return `已讀完 ${props.item.readCount}／${props.item.collection.memberCount}`
  const total = props.item.collection.members.reduce((sum, member) => sum + member.pageCount, 0)
  return props.item.mergedProgress ? `第 ${props.item.mergedProgress.page + 1}／${total} 頁` : `${total} 頁`
})
function activate() { if (merged.value) emit('read', props.item); else emit('open', props.item) }
function selectAction(key: CollectionAction) {
  if (key === 'read') { emit('read', props.item); return }
  if (key === 'open') { emit('open', props.item); return }
  if (key === 'edit') { emit('edit', props.item); return }
  if (key === 'dissolve') { emit('dissolve', props.item); return }
  emit('delete', props.item)
}
</script>

<template>
  <li ref="element" class="comic-row collection-row" data-row-action-reveal-scope>
    <ComicSelectBox
      v-if="selecting"
      :selected="selected"
      :title="`集合 ${collection.title}`"
      @toggle="extend => entries.toggleSelected(item.id, extend)"
    />
    <div class="comic-row-cover">
      <img v-if="cover && !failed" :src="cover" :alt="`${collection.title}，封面`" />
      <LibraryBig v-else :size="18" :stroke-width="1.2" aria-hidden="true" />
    </div>
    <div class="comic-row-main">
      <button v-if="selecting" type="button" class="comic-row-title" @click="event => entries.toggleSelected(item.id, event.shiftKey)">{{ collection.title }}</button>
      <button v-else type="button" class="comic-row-title" @click="activate">{{ collection.title }}</button>
      <p class="comic-row-meta"><span>{{ merged ? '合併冊' : '集合' }} · {{ collectionBadge(item) }}</span></p>
    </div>
    <div class="comic-row-aside">
      <ComicStatusDot v-if="status" :status="status" size="sm" />
      <span class="comic-row-pages">{{ pagesText }}</span>
      <Progress v-if="progress" class="comic-row-progress" size="sm" color="primary" :value="progress.percent" :aria-label="progress.label" />
      <span v-else class="comic-row-progress is-empty" aria-hidden="true" />
      <CollectionCardMenu class="comic-row-menu" :title="collection.title" :merged="merged" :data-collection-id="collection.id" @select="selectAction" />
    </div>
  </li>
</template>
